import { Link } from "react-router-dom";
import { ArrowRight, Calculator, FileText } from "lucide-react";
import Seo from "@/components/Seo";
import ShortAnswer from "@/components/ShortAnswer";
import { HUB_PATHS, EMPLOYER_GUIDES, CHECKED_ON } from "@/data/equality";
import { Hero, GuideList, CheckerCard, breadcrumb, linkClass } from "./shared";

const LIVE_TOOLS = [
  {
    to: "/resources/harassment-risk-assessment-template",
    icon: FileText,
    title: "Harassment risk assessment template",
    text: "For the duty to take all reasonable steps to prevent sexual harassment, and the third-party harassment duty, from 30 October 2026.",
  },
  {
    to: "/calculators/pro-rata-salary-calculator",
    icon: Calculator,
    title: "Pro-rata salary calculator",
    text: "Part-time pay worked out properly, so part-timers are paid in proportion to full-timers.",
  },
];

export default function EmployersHub() {
  const origin = window.location.origin;
  const pageUrl = `${origin}${HUB_PATHS.employers}`;
  return (
    <main data-testid="equality-employers-hub">
      <Seo
        title="Equality at Work for Employers: Best Practice That Pays (UK) | Electra HR"
        siteName="Electra HR"
        description="Free equality guides for UK employers: what the Equality Act 2010 protects, reasonable adjustments, pregnancy and maternity, menopause and flexible working, with the commercial case for getting it right."
        image="/og/default.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": pageUrl,
              url: pageUrl,
              name: "Equality at work for employers",
              isPartOf: { "@id": `${origin}/#website` },
              publisher: { "@id": `${origin}/#organization` },
              dateModified: CHECKED_ON.iso,
            },
            breadcrumb(origin, [
              { name: "Home", path: "/" },
              { name: "Equality at work", path: HUB_PATHS.home },
              { name: "For employers", path: HUB_PATHS.employers },
            ]),
          ],
        }}
      />

      <Hero
        testid="employers-hero"
        eyebrow="Equality at work · For employers"
        title="Equality that pays."
        accent="Get it right once."
        intro="Discrimination compensation has no cap, and most claims start with a small decision nobody thought twice about. These guides show the fair way through, and where it saves you money."
      />

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x max-w-5xl">
          <ShortAnswer path={HUB_PATHS.employers} className="mb-12" />

          <CheckerCard to={HUB_PATHS.checker} testid="employers-checker-card" />

          <h2 className="mt-16 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Guides for employers</h2>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink/70">
            Each guide has a twin in the <Link to={HUB_PATHS.employees} className={linkClass}>hub for people at work</Link>, covering the same law from the other side of the desk.
          </p>
          <div className="mt-8">
            <GuideList guides={EMPLOYER_GUIDES} testid="employer-guides" />
          </div>

          <h2 className="mt-16 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Free tools already live</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {LIVE_TOOLS.map(({ to, icon: Icon, title, text }) => (
              <Link key={to} to={to} className="group border-2 border-ink bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#F472B6]">
                <Icon size={20} className="text-pink" />
                <p className="mt-4 font-display text-xl font-semibold tracking-tight">{title}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink/70">{text}</p>
                <span className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-ink/60 group-hover:text-ink">
                  Open <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-14 border-l-4 border-pink bg-pink/10 p-6 text-base leading-relaxed text-ink/80">
            Guides explain the law; real situations have contracts, history and people in them. If you need help applying it, see{" "}
            <Link to="/services" className={linkClass}>Electra HR's fixed-fee services</Link>.
          </div>
        </div>
      </section>
    </main>
  );
}
